import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useLayoutEffect, useMemo } from 'react';
import Header from './Header';
import { mapNamesToWidths } from '../utils/mapNamesToWidths';
import TableBody from './TableBody';
import { getTableMinWidth, setAllWidths } from '../../resizing/redux/widthsSlice';
import { ProvideObserver } from '../../virtualization/context/observerContext';
import { useTableDispatch, useTableSelector } from '../../redux/reduxHooks';
import SelectAllCheckboxHeader from '../../selection/components/SelectAllCheckboxHeader';
import TableTopBar from './TableTopBar';
import { SELECTION_COLUMN_WIDTH, TABLE_PAGINATOR_HEIGHT, TABLE_TOP_BAR_HEIGHT } from '../constants/sizes';
import UIBox from 'UIComponents/layout/UIBox';
import Paginator from '../../pagination/components/Paginator';
import UILoadingOverlay from 'UIComponents/loading/UILoadingOverlay';
import { TableScrollContainer, TableBox, FullFlex } from './StyledTableComponents';
import { getColumnKey } from '../utils/getColumnKey';
import { useWatchTableContainer } from '../hooks/useWatchTableContainer';
import { getSelectedRowIds, getShouldShowBulkActionsContainer } from '../../selection/redux/selectionSlice';
import SelectQueryRow from '../../selection/components/SelectQueryRow';
const headerZIndexStyle = {
  zIndex: 3
};

const Table = ({
  getRowId,
  rows,
  columns,
  sorts,
  selectable,
  pageable,
  loading,
  pageIndex,
  pageSize,
  pageSizes,
  totalRowCount,
  BulkActionsComponent,
  RowComponent,
  TableComponent,
  headerLeftSlot,
  headerRightSlot,
  noRowsSlot,
  onColumnWidthChange,
  onColumnOrderChange,
  onPageIndexChange,
  onPageSizeChange,
  onSortsChange,
  canSelectAllRecords,
  numberOfFrozenColumns,
  CustomSelectAllComponent
}) => {
  const rowIds = useMemo(() => rows.map(getRowId), [getRowId, rows]);
  const {
    observer,
    handleTableContainerRef
  } = useWatchTableContainer(rowIds);
  const dispatch = useTableDispatch();
  useLayoutEffect(() => {
    dispatch(setAllWidths(mapNamesToWidths(columns)));
  }, [columns, dispatch]);
  const sortMap = useMemo(() => sorts.reduce((acc, {
    columnNamespace,
    columnName,
    direction
  }) => {
    acc[getColumnKey(columnNamespace, columnName)] = direction;
    return acc;
  }, {}), [sorts]);
  const tableMinWidth = useTableSelector(getTableMinWidth) + (selectable ? SELECTION_COLUMN_WIDTH : 0);
  const tableStyle = useMemo(() => ({
    minWidth: tableMinWidth
  }), [tableMinWidth]); // TODO: Add multisort when the index page can handle it

  const handleSortChange = useCallback(({
    columnNamespace,
    columnName,
    direction
  }) => {
    onSortsChange({
      nextSorts: [{
        columnNamespace,
        columnName,
        direction
      }]
    });
  }, [onSortsChange]);
  const selectedRowIds = useTableSelector(getSelectedRowIds);
  const isCurrentPageSelected = useMemo(() => rowIds.length > 0 && rowIds.every(rowId => selectedRowIds.has(rowId)), [rowIds, selectedRowIds]);
  const hasMorePages = totalRowCount > rowIds.length;
  const showSelectAllRow = canSelectAllRecords && isCurrentPageSelected && hasMorePages;
  const showTopBar = useTableSelector(getShouldShowBulkActionsContainer) || headerLeftSlot || headerRightSlot;
  const requiredTableProps = useMemo(() => ({
    // HACK: This is required for compat with onboarding tours. Once
    // we have renamed the contact "name" column, we can PR their library
    // to use the actual external id selector and remove this one.
    'data-onboarding': 'grid-table',
    'data-table-external-id': 'framework-data-table',
    'data-test-id': 'framework-data-table',
    'data-test-loading': loading,
    style: tableStyle
  }), [loading, tableStyle]);
  return /*#__PURE__*/_jsxs(FullFlex, {
    direction: "column",
    "data-test-id": "framework-data-table-container",
    children: [showTopBar && /*#__PURE__*/_jsx(UIBox, {
      shrink: 0,
      basis: TABLE_TOP_BAR_HEIGHT,
      className: "width-100",
      children: /*#__PURE__*/_jsx(TableTopBar, {
        headerLeftSlot: headerLeftSlot,
        headerRightSlot: headerRightSlot,
        BulkActionsComponent: BulkActionsComponent,
        totalRowCount: totalRowCount
      })
    }), /*#__PURE__*/_jsxs(TableBox, {
      showPagination: pageable,
      showTopBar: showTopBar,
      children: [loading && /*#__PURE__*/_jsx(UILoadingOverlay, {
        "data-test-id": "table-loading",
        contextual: true
      }), rows.length > 0 ? /*#__PURE__*/_jsx(TableScrollContainer, {
        ref: handleTableContainerRef,
        children: /*#__PURE__*/_jsx(ProvideObserver, {
          value: observer,
          children: /*#__PURE__*/_jsxs(TableComponent, {
            requiredTableProps: requiredTableProps,
            children: [/*#__PURE__*/_jsxs("thead", {
              style: headerZIndexStyle,
              children: [/*#__PURE__*/_jsxs("tr", {
                children: [selectable && /*#__PURE__*/_jsx(SelectAllCheckboxHeader, {
                  numberOfFrozenColumns: numberOfFrozenColumns,
                  rowIds: rowIds
                }), columns.map((column, index) => {
                  const key = getColumnKey(column.columnNamespace, column.columnName);
                  return /*#__PURE__*/_jsx(Header, {
                    column: column,
                    columnIndex: index,
                    isLastColumn: index === columns.length - 1,
                    sortDirection: sortMap[key],
                    onSortChange: handleSortChange,
                    onColumnWidthChange: onColumnWidthChange,
                    onColumnOrderChange: onColumnOrderChange,
                    numberOfFrozenColumns: numberOfFrozenColumns
                  }, key);
                })]
              }), showSelectAllRow && /*#__PURE__*/_jsx(SelectQueryRow, {
                rowIds: rowIds,
                totalRowCount: totalRowCount,
                columnCount: columns.length + (selectable ? 1 : 0),
                CustomSelectAllComponent: CustomSelectAllComponent
              })]
            }), /*#__PURE__*/_jsx(TableBody, {
              RowComponent: RowComponent,
              getRowId: getRowId,
              rows: rows,
              columns: columns,
              selectable: selectable,
              numberOfFrozenColumns: numberOfFrozenColumns
            })]
          })
        })
      }) : noRowsSlot]
    }), pageable && /*#__PURE__*/_jsx(UIBox, {
      shrink: 0,
      basis: TABLE_PAGINATOR_HEIGHT,
      className: "width-100",
      children: /*#__PURE__*/_jsx(Paginator, {
        pageIndex: pageIndex,
        pageSize: pageSize,
        pageSizes: pageSizes,
        totalRowCount: totalRowCount,
        onPageIndexChange: onPageIndexChange,
        onPageSizeChange: onPageSizeChange
      })
    })]
  });
};

export default Table;